import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faEye,
  faDownload,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { toast } from '@/lib/swal';
import { usePermissions } from '@/hooks/use-permissions';
import { inspectionReportService } from '@/services/inspection-report.service';
import {
  REPORT_PERMISSIONS,
  REPORT_STATUS_BADGE,
  REPORT_STATUS_LABELS,
  canEditReport,
  reportNumberFormatPattern,
} from '../../report/report-workflow';
import { SectionHeader } from '../SectionShell';
import { ReportReadinessBanner } from './ReportReadinessBanner';
import type { InspectionWarrantyStatus } from '@/services/inspection.service';
import { ReportMetadataCard } from './ReportMetadataCard';
import { ReportPhotoSelector } from './ReportPhotoSelector';
import { ReportFindingSelector } from './ReportFindingSelector';
import { ReportSectionComposer } from './ReportSectionComposer';
import { ReportWorkflowActions } from './ReportWorkflowActions';
import { ReportHistoryList } from './ReportHistoryList';

interface Props {
  inspectionId: string;
  /**
   * The inspection's current warranty coverage, for the Edit Report dropdown.
   *
   * Passed down rather than fetched: the detail page already holds the
   * inspection, so threading the value costs nothing, while adding it to the
   * REPORT response would mean a field on `InspectionReportResponseDto` that
   * does not live on `inspection_reports` and a changed mapper signature at
   * every call site. Optional so the section still renders standalone.
   */
  warrantyStatus?: InspectionWarrantyStatus;
}

/** Pulls `error.message` out of the API's error envelope, if it is there. */
function envelopeMessage(body: unknown): string | null {
  const msg = (body as { error?: { message?: string } } | null | undefined)?.error?.message;
  return typeof msg === 'string' && msg.length > 0 ? msg : null;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    return envelopeMessage(error.response?.data) ?? fallback;
  }
  return fallback;
}

/**
 * Same, for the two PDF routes.
 *
 * They are requested with `responseType: 'blob'`, and axios applies that to
 * *error* bodies as well as successful ones — so a 409's JSON envelope arrives
 * as a `Blob` and `envelopeMessage` sees `undefined` on it. That is how the
 * backend's actionable sentence ("Generate the report before downloading — it
 * has no report number yet.") reached the browser and was replaced by the
 * useless generic "Failed to download report". Read the Blob back before
 * inspecting it.
 */
async function extractPdfMsg(error: unknown, fallback: string): Promise<string> {
  if (!axios.isAxiosError(error)) return fallback;
  const body: unknown = error.response?.data;
  if (body instanceof Blob) {
    try {
      return envelopeMessage(JSON.parse(await body.text())) ?? fallback;
    } catch {
      return fallback; // not JSON (a truncated stream, an HTML proxy page…)
    }
  }
  return envelopeMessage(body) ?? fallback;
}

function fmtDateTime(v: string | null): string {
  if (!v) return '—';
  return new Date(v).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      {children}
    </div>
  );
}

export function InspectionReportSection({ inspectionId, warrantyStatus }: Props) {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const [pdfBusy, setPdfBusy] = useState<'preview' | 'download' | null>(null);
  // Collapsed on load — see the accordion below.
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const sectionsPanelId = useId();
  const [photosOpen, setPhotosOpen] = useState(false);
  const photosPanelId = useId();

  const {
    data: report,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['report', inspectionId],
    retry: false,
    queryFn: async () => {
      try {
        return await inspectionReportService.getReport(inspectionId);
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404) return null;
        throw e;
      }
    },
  });

  const { data: readiness } = useQuery({
    queryKey: ['report-readiness', inspectionId],
    queryFn: () => inspectionReportService.getReadiness(inspectionId),
    staleTime: 10_000,
  });

  // The display-only projection of the tenant's report configuration — a
  // report author can read this with the report permission they already hold,
  // where the full settings endpoint needs the company-profile permission an
  // engineer has no reason to carry. Used only to describe the format of a
  // number that has not been allocated yet; an issued number always comes from
  // the report row itself.
  const { data: reportSettings } = useQuery({
    queryKey: ['report-number-presentation'],
    queryFn: () => inspectionReportService.getNumberPresentation(),
    staleTime: 60_000,
  });

  // The server owns this now — it is the only side that can see the frozen
  // snapshot policy the state depends on.
  const procedureState = readiness?.procedureState;

  const createMutation = useMutation({
    mutationFn: () => inspectionReportService.createDraft(inspectionId),
    onSuccess: () => {
      toast.success('Report draft created');
      void qc.invalidateQueries({ queryKey: ['report', inspectionId] });
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to create report draft')),
  });

  const runPdf = async (kind: 'preview' | 'download') => {
    setPdfBusy(kind);
    try {
      if (kind === 'preview') await inspectionReportService.openPreview(inspectionId);
      else await inspectionReportService.download(inspectionId);
    } catch (e) {
      toast.error(await extractPdfMsg(e, `Failed to ${kind} report`));
    } finally {
      setPdfBusy(null);
    }
  };

  const header = <SectionHeader icon={faFileLines} title="Inspection Report" />;

  if (isLoading) {
    return (
      <Card>
        {header}
        <div className="space-y-3 p-5" data-testid="report-loading">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-muted" />
          ))}
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        {header}
        <p className="p-5 text-sm text-muted-foreground">Unable to load the report.</p>
      </Card>
    );
  }

  // No report yet → offer to create the draft (authors only).
  if (!report) {
    const canCreate = has(REPORT_PERMISSIONS.submit);
    // Report generation must not start until every mandatory readiness
    // requirement passes. The backend re-validates readiness on generate
    // (render service) as the source of truth; this gates the UI to match.
    const isReady = readiness?.canGenerate ?? false;
    return (
      <Card>
        {header}
        <div className="space-y-3 p-5" data-testid="report-empty">
          {readiness && (
            <ReportReadinessBanner readiness={readiness} procedureState={procedureState} />
          )}
          <p className="text-sm text-muted-foreground">
            No report has been started for this inspection.
          </p>
          {canCreate && (
            <>
              <Button
                type="button"
                data-testid="start-report"
                disabled={createMutation.isPending || !isReady}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending ? 'Creating…' : 'Start Report'}
              </Button>
              {!isReady && (
                <p className="text-xs text-muted-foreground">
                  Complete all readiness requirements above before starting the report.
                </p>
              )}
            </>
          )}
        </div>
      </Card>
    );
  }

  const editable = canEditReport(report, has);

  // Download is the one PDF route that requires the permanent report number:
  // the filename is built from it and the act is audited as issuing a numbered
  // document, so the backend rejects a numberless report with 409 (see
  // `inspection-report-render.service.ts` — `build(id, requireNumber = true)`).
  // The number is allocated by Generate. Until that has run the button was
  // still offered and every click was a guaranteed failure, so the state is
  // mirrored here — disabled with the reason rather than hidden, so the
  // DRAFT → Generate → Download sequence stays visible instead of a control
  // silently appearing later. This mirrors the backend rule; it does not
  // replace it — the server still re-checks and 409s.
  const downloadBlockedReason: string | null =
    report.reportNumber === null || report.status === 'DRAFT'
      ? 'Generate the report first — a downloadable PDF needs its permanent report number.'
      : null;

  return (
    <Card>
      {header}
      <div className="space-y-5 p-5" data-testid="report-section">
        {/* Status header */}
        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-2"
          data-testid="report-status-header"
        >
          <div>
            <p className="text-xs text-muted-foreground">Report Number</p>
            {report.reportNumber ? (
              // Issued and immutable: always the stored value, never re-derived
              // from settings — later settings changes must not appear to
              // rewrite a number that has already been allocated.
              <p className="font-mono text-sm font-semibold text-foreground">
                {report.reportNumber}
              </p>
            ) : (
              <div data-testid="report-number-pending">
                <p className="text-sm font-semibold text-muted-foreground">Not yet assigned</p>
                {reportSettings && (
                  <p className="font-mono text-xs text-muted-foreground">
                    Format {reportNumberFormatPattern(reportSettings)}
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Status</p>
            <span
              className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${REPORT_STATUS_BADGE[report.status]}`}
            >
              {REPORT_STATUS_LABELS[report.status]}
            </span>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Customer Visibility</p>
            <p className="text-sm text-foreground">
              {report.publishedToCustomer ? 'Published' : 'Not published'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Template</p>
            {/*
              B6.11 (G-1). Was `Default Inspection Report v1.0` from the stale
              template_name/template_version columns, which hold the renderer's
              build-time constants rather than the template that drew the
              report. Now the frozen layout's version — the same value the PDF
              footer prints — so staff and the customer see identical
              provenance. Null until the first generate freezes a layout.
            */}
            <p className="text-sm text-foreground">
              {report.layoutVersion !== null ? `Report v${report.layoutVersion}` : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Generated</p>
            <p className="text-sm text-foreground">{fmtDateTime(report.issuedAt)}</p>
          </div>
          <div className="ml-auto flex gap-2">
            {has(REPORT_PERMISSIONS.generate) && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={pdfBusy !== null}
                onClick={() => runPdf('preview')}
              >
                <FontAwesomeIcon icon={faEye} className="mr-1.5 h-3 w-3" />
                {pdfBusy === 'preview' ? 'Opening…' : 'Preview'}
              </Button>
            )}
            {has(REPORT_PERMISSIONS.download) && (
              // Right-anchored and wrapping: this button sits at the end of the
              // header row, where the default centred `whitespace-nowrap`
              // bubble runs off the viewport and clips the reason.
              <Tooltip
                label={downloadBlockedReason ?? 'Download the report PDF'}
                className="left-auto right-0 w-64 translate-x-0 whitespace-normal text-left"
              >
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={pdfBusy !== null || downloadBlockedReason !== null}
                  aria-disabled={pdfBusy !== null || downloadBlockedReason !== null}
                  onClick={() => runPdf('download')}
                >
                  <FontAwesomeIcon icon={faDownload} className="mr-1.5 h-3 w-3" />
                  {pdfBusy === 'download' ? 'Downloading…' : 'Download'}
                </Button>
              </Tooltip>
            )}
          </div>
        </div>

        {report.status === 'REJECTED' && report.rejectionReason && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
            <span className="font-semibold">Rejected:</span> {report.rejectionReason}
          </div>
        )}

        {readiness && (
          <ReportReadinessBanner readiness={readiness} procedureState={procedureState} />
        )}

        <ReportWorkflowActions
          inspectionId={inspectionId}
          report={report}
          readiness={readiness}
          has={has}
        />

        <div className="border-t border-border pt-4">
          <ReportMetadataCard
            inspectionId={inspectionId}
            report={report}
            canEdit={editable}
            {...(warrantyStatus === undefined ? {} : { warrantyStatus })}
          />
        </div>

        <div className="border-t border-border pt-4">
          <ReportFindingSelector inspectionId={inspectionId} canEdit={editable} />
        </div>

        {/*
          Reference Photos is a picker, a row editor and an uploader, so it is
          collapsed on load for the same reason section composition is: the rest
          of the report stays reachable without scrolling past it. The composer
          mounts only when expanded and keeps its own loading, error, frozen and
          save behaviour — expanding it reads, and writes nothing.
        */}
        <div className="border-t border-border pt-4">
          <div className="overflow-hidden rounded-lg border border-border">
            <button
              type="button"
              onClick={() => setPhotosOpen((open) => !open)}
              aria-expanded={photosOpen}
              aria-controls={photosPanelId}
              className="flex min-h-[48px] w-full items-center gap-3 bg-muted/20 px-4 py-3 text-left transition hover:bg-muted/40"
              data-testid="report-photos-accordion-toggle"
            >
              <FontAwesomeIcon
                icon={photosOpen ? faChevronDown : faChevronRight}
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              />
              <span className="flex-1 text-sm font-semibold text-foreground">Reference Photos</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {photosOpen ? 'Hide' : 'Show'}
              </span>
            </button>
            {photosOpen && (
              <div id={photosPanelId} className="bg-background p-4">
                <ReportPhotoSelector inspectionId={inspectionId} canEdit={editable} hideHeading />
              </div>
            )}
          </div>
        </div>

        {/*
          Section composition is the least-used control on this screen and the
          tallest, so it is collapsed on load and the rest of the report stays
          reachable without scrolling past it. The composer itself is untouched:
          it mounts only when expanded, and keeps its own loading, error,
          legacy/frozen, permission and save behaviour exactly as before.
        */}
        <div className="border-t border-border pt-4">
          <div className="overflow-hidden rounded-lg border border-border">
            <button
              type="button"
              onClick={() => setSectionsOpen((open) => !open)}
              aria-expanded={sectionsOpen}
              aria-controls={sectionsPanelId}
              className="flex min-h-[48px] w-full items-center gap-3 bg-muted/20 px-4 py-3 text-left transition hover:bg-muted/40"
              data-testid="report-sections-accordion-toggle"
            >
              <FontAwesomeIcon
                icon={sectionsOpen ? faChevronDown : faChevronRight}
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              />
              <span className="flex-1 text-sm font-semibold text-foreground">Report Sections</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {sectionsOpen ? 'Hide' : 'Show'}
              </span>
            </button>
            {sectionsOpen && (
              <div id={sectionsPanelId} className="bg-background p-4">
                <ReportSectionComposer inspectionId={inspectionId} canEdit={editable} hideHeading />
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground">Workflow History</h3>
          <ReportHistoryList inspectionId={inspectionId} />
        </div>
      </div>
    </Card>
  );
}
